import React, { useState, useEffect, useRef } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db, SECRETS_ENV } from '../../../../../Config/firebase';
import { toast } from 'react-toastify';
import axios from 'axios';
// Import Shaka Player
import shaka from 'shaka-player/dist/shaka-player.compiled';

interface RecordedSessionsProps {
    serviceProviderId: string;
    maxItems?: number; // Optional limit for showing recent recordings
}

interface RecordingDocument {
    id: string;
    created_at: any; // Firestore timestamp
    created_by: string;
    filename: string;
    layout: string;
    meeting_title: string;
    original_room_name: string;
    recording_id: string;
    recording_type: string;
    resolution: string;
    room_name: string;
    s3_bucket: string;
    s3_key: string;
    started_at: string;
    status: string;
    stop_response?: string;
    stopped_at: any; // Firestore timestamp
    tenant_id: string;
    updated_at: any; // Firestore timestamp
    playback_url?: string; // Transcoded URL for playback
    transcoded_path?: string; // HLS transcoded path
    transcoding_job_id?: string; // Transcoding job ID
}

interface PlaybackUrlResponse {
    success: boolean;
    recordingDocPath?: string;
    type?: 'hls' | 'mp4';
    url?: string;
    hls?: {
        manifestUrl: string; // Backend returns manifestUrl, not masterUrl
    };
    mp4?: {
        url: string;
    };
    expiresAt?: string;
    error?: string; // Error message for failure cases
}

const RecordedSessions: React.FC<RecordedSessionsProps> = ({
    serviceProviderId,
    maxItems
}) => {
    const [recordings, setRecordings] = useState<RecordingDocument[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRecording, setSelectedRecording] = useState<RecordingDocument | null>(null);
    const [showPlayerModal, setShowPlayerModal] = useState(false);
    const [filterStatus, setFilterStatus] = useState<'all' | 'stopped' | 'processing'>('all');
    const [playbackData, setPlaybackData] = useState<PlaybackUrlResponse | null>(null);
    const [isLoadingPlaybackUrl, setIsLoadingPlaybackUrl] = useState(false);
    const [deletingRecordingId, setDeletingRecordingId] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<shaka.Player | null>(null);

    // Fetch recordings from Firestore in real-time
    useEffect(() => {
        if (!serviceProviderId) return;

        setIsLoading(true);

        const recordingsRef = collection(db, 'ServiceProviders', serviceProviderId, 'Recordings');
        const q = query(recordingsRef, orderBy('created_at', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const recordingsData: RecordingDocument[] = [];

            snapshot.forEach((doc) => {
                const data = doc.data();
                recordingsData.push({
                    id: doc.id,
                    created_at: data.created_at,
                    created_by: data.created_by,
                    filename: data.filename,
                    layout: data.layout,
                    meeting_title: data.meeting_title,
                    original_room_name: data.original_room_name,
                    recording_id: data.recording_id,
                    recording_type: data.recording_type,
                    resolution: data.resolution,
                    room_name: data.room_name,
                    s3_bucket: data.s3_bucket,
                    s3_key: data.s3_key,
                    started_at: data.started_at,
                    status: data.status,
                    stop_response: data.stop_response,
                    stopped_at: data.stopped_at,
                    tenant_id: data.tenant_id,
                    updated_at: data.updated_at,
                    playback_url: `https://${data.s3_bucket}.s3.amazonaws.com/${data.s3_key}`, // Construct playback URL
                    transcoded_path: data.transcoded_path, // HLS transcoded path
                    transcoding_job_id: data.transcoding_job_id // Transcoding job ID
                });
            });

            setRecordings(recordingsData);
            setIsLoading(false);
        }, (error) => {
            console.error('Error fetching recordings:', error);
            toast.error('Failed to load recordings');
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [serviceProviderId]);

    const deleteRecording = async (recordingId: string, recordingTitle: string) => {
        if (!confirm(`Are you sure you want to delete "${recordingTitle}"? This action cannot be undone.`)) {
            return;
        }

        try {
            setDeletingRecordingId(recordingId);
            const API_ENDPOINT = SECRETS_ENV.API_END_POINT;
            
            // Call backend API to delete from S3 and Firestore
            // New API format: providerId + recordingDocId (not full path)
            const response = await axios.post(`${API_ENDPOINT}/api/meet/deleteRecording`, {
                providerId: serviceProviderId,
                recordingDocId: recordingId
            }, {
                validateStatus: (status) => {
                    // Accept 200, 400, 404, 500 as valid responses (not throw errors)
                    return status === 200 || status === 400 || status === 404 || status === 500;
                }
            });

            // Handle different response codes
            if (response.status === 200 && response.data.success) {
                // Success: Recording and S3 assets deleted
                toast.success(response.data.message || 'Recording deleted successfully!');
                // Backend handles Firestore deletion, so we don't need to delete locally
            } else if (response.status === 400) {
                // 400: Missing params
                const errorMsg = response.data.error || 'Missing required parameters';
                toast.error(errorMsg);
                console.error('Delete recording error (400):', errorMsg);
            } else if (response.status === 404) {
                // 404: Recording not found
                const errorMsg = response.data.error || 'Recording not found';
                toast.error(errorMsg);
                console.error('Delete recording error (404):', errorMsg);
            } else if (response.status === 500) {
                // 500: Internal server error
                const errorMsg = response.data.error || 'Internal server error';
                toast.error(errorMsg);
                console.error('Delete recording error (500):', errorMsg);
            } else {
                // Unexpected response
                throw new Error(response.data.message || response.data.error || 'Delete failed');
            }
        } catch (error: any) {
            console.error('Error deleting recording:', error);
            // Handle network errors or other exceptions
            if (error.response) {
                // Response received but status code not handled above
                toast.error(error.response.data?.error || error.response.data?.message || 'Failed to delete recording');
            } else {
                // Network error or other exception
                toast.error(error.message || 'Failed to delete recording');
            }
        } finally {
            setDeletingRecordingId(null);
        }
    };

    const playRecording = async (recording: RecordingDocument) => {
        setSelectedRecording(recording);
        setShowPlayerModal(true);
        setPlaybackData(null);
        setIsLoadingPlaybackUrl(true);

        try {
            const API_ENDPOINT = SECRETS_ENV.API_END_POINT;
            
            console.log('🎬 Requesting playback URL for:', {
                recordingId: recording.id,
                providerId: serviceProviderId,
                status: recording.status,
                transcoded_path: recording.transcoded_path,
                s3_key: recording.s3_key
            });
            
            // Fetch tokenized playback URL from API
            // The API will return a tokenized URL from the original S3 URL or transcoded HLS path
            // Request body: { "providerId": "...", "recordingDocId": "..." }
            const response = await axios.post<PlaybackUrlResponse>(
                `${API_ENDPOINT}/api/meet/getRecordingPlaybackUrl`,
                {
                    providerId: serviceProviderId,
                    recordingDocId: recording.id
                }
            );

            // Handle success response
            if (response.data.success) {
                const playbackResponse = response.data;
                
                // Validate that at least one playback format is available
                // API can return either HLS or MP4 or both
                const hasHls = !!playbackResponse.hls?.manifestUrl;
                const hasMp4 = !!playbackResponse.mp4?.url;
                
                if (!hasHls && !hasMp4) {
                    throw new Error('No playback URL available in response. Neither HLS nor MP4 URL found.');
                }
                
                console.log('✅ Playback URL received:', {
                    type: playbackResponse.type,
                    hasHls,
                    hasMp4,
                    expiresAt: playbackResponse.expiresAt
                });
                
                setPlaybackData(playbackResponse);
            } else {
                // API returned success: false or error object
                const errorMessage = response.data.error || 'Failed to get playback URL';
                throw new Error(errorMessage);
            }
        } catch (error: any) {
            console.error('❌ Error fetching playback URL:', error);
            
            // Handle different error cases
            if (error.response) {
                // Server responded with error status
                const status = error.response.status;
                const errorData = error.response.data;
                
                if (status === 404) {
                    toast.error('Recording not found. It may have been deleted or the path is incorrect.');
                } else if (errorData?.error) {
                    // API returned error object: { "error": "Internal server error" }
                    toast.error(errorData.error);
                } else if (errorData?.message) {
                    toast.error(errorData.message);
                } else {
                    toast.error(`Failed to load recording playback URL (${status})`);
                }
            } else if (error.request) {
                // Request was made but no response received
                toast.error('Network error. Please check your connection and try again.');
            } else {
                // Error in request setup or other error
                toast.error(error.message || 'Failed to load recording playback URL');
            }
            
            // Keep modal open but show error state (playbackData will remain null)
        } finally {
            setIsLoadingPlaybackUrl(false);
        }
    };

    // Cleanup Shaka Player when modal closes
    useEffect(() => {
        if (!showPlayerModal && playerRef.current) {
            try {
                playerRef.current.destroy();
            } catch (error) {
                console.error('Error destroying Shaka Player:', error);
            }
            playerRef.current = null;
        }
    }, [showPlayerModal]);

    // Setup Shaka Player for HLS playback when playback data is available
    useEffect(() => {
        console.log('🎥 Video setup effect triggered:', {
            hasPlaybackData: !!playbackData,
            hasVideoRef: !!videoRef.current,
            showPlayerModal,
            playbackDataType: playbackData?.type,
            hasHls: !!playbackData?.hls?.manifestUrl,
            hasMp4: !!playbackData?.mp4?.url
        });
        
        if (!playbackData || !videoRef.current || !showPlayerModal) {
            console.log('⏭️ Skipping video setup - missing requirements');
            return;
        }

        const video = videoRef.current;
        
        console.log('📹 Video element found, starting setup with Shaka Player');
        
        // Cleanup existing player if any
        if (playerRef.current) {
            try {
                playerRef.current.destroy();
            } catch (error) {
                console.error('Error destroying existing player:', error);
            }
            playerRef.current = null;
        }
        
        // Clear any existing src to prevent conflicts with Shaka Player
        video.src = '';
        video.removeAttribute('src');
        
        // Check if HLS is available and preferred
        if (playbackData.type === 'hls' && playbackData.hls?.manifestUrl) {
            console.log('🎬 Setting up Shaka Player for HLS playback:', {
                manifestUrl: playbackData.hls.manifestUrl
            });
            
            try {
                // Install Shaka Player polyfills if needed
                shaka.polyfill.installAll();
                
                // Check browser support
                if (!shaka.Player.isBrowserSupported()) {
                    console.warn('⚠️ Shaka Player not supported, falling back to MP4');
                    if (playbackData.mp4?.url) {
                        video.src = playbackData.mp4.url;
                    } else {
                        toast.error('HLS playback not supported in this browser');
                    }
                    return;
                }

                // Create Shaka Player instance (using attach method to avoid deprecation warning)
                const player = new shaka.Player();
                player.attach(video);
                playerRef.current = player;

                // The manifestUrl is already a pre-signed API endpoint that serves the manifest
                // with all segment URLs pre-signed, so we don't need to extract signing params
                // Backend's hlsManifest endpoint handles all URL signing automatically

                // Configure player
                player.configure({
                    streaming: {
                        bufferingGoal: 30,
                        rebufferingGoal: 2,
                        bufferBehind: 30,
                    },
                    abr: {
                        enabled: true,
                    },
                });

                // No need for request filter - backend's hlsManifest endpoint already pre-signs
                // all segment URLs in the manifest, so Shaka Player will use those pre-signed URLs directly

                // Error handling
                player.addEventListener('error', (event: any) => {
                    console.error('❌ Shaka Player error:', event.detail);
                    const error = event.detail;
                    const isCorsError = error && (
                        error.code === 1002 || // NETWORK_ERROR
                        error.code === 1001 || // REQUEST_ERROR
                        (error.message && error.message.includes('CORS')) ||
                        (error.message && error.message.includes('Access-Control'))
                    );
                    
                    // Try to recover from errors
                    if (error && error.severity === shaka.util.Error.Severity.CRITICAL) {
                        if (isCorsError) {
                            console.log('🚫 CORS error detected, falling back to MP4');
                        } else {
                            console.log('🔄 Critical error, falling back to MP4');
                        }
                        try {
                            player.destroy();
                        } catch (e) {
                            console.error('Error destroying player:', e);
                        }
                        playerRef.current = null;
                        
                        // Fallback to MP4
                        if (playbackData.mp4?.url) {
                            try {
                                // Detach player from video element first
                                player.detach();
                                player.destroy();
                            } catch (e) {
                                console.error('Error destroying player:', e);
                            }
                            playerRef.current = null;
                            
                            // Clear and set MP4 source
                            video.src = '';
                            video.removeAttribute('src');
                            setTimeout(() => {
                                console.log('📹 Using MP4 due to CORS restrictions:', playbackData.mp4.url);
                                video.src = playbackData.mp4.url;
                                video.load();
                                toast.info('Switched to MP4 playback');
                            }, 100);
                        } else {
                            toast.error('Failed to load video');
                        }
                    } else if (error && error.severity === shaka.util.Error.Severity.RECOVERABLE) {
                        console.log('🔄 Recoverable error, player will attempt to recover');
                    }
                });

                // Load the HLS manifest
                // The manifestUrl points to the backend API endpoint that serves the manifest
                // with all segment URLs pre-signed
                player.load(playbackData.hls.manifestUrl)
                    .then(() => {
                        console.log('✅ Shaka Player loaded HLS manifest successfully');
                    })
                    .catch((error: any) => {
                        console.error('❌ Error loading HLS manifest:', error);
                        
                        const isCorsError = error && (
                            error.code === 1002 || // NETWORK_ERROR
                            error.code === 1001 || // REQUEST_ERROR
                            (error.message && error.message.includes('CORS')) ||
                            (error.message && error.message.includes('Access-Control')) ||
                            (error.message && error.message.includes('blocked by CORS'))
                        );
                        
                        const is403Error = error && (
                            error.code === 1001 || // REQUEST_ERROR
                            (error.data && error.data[0] && error.data[0].status === 403) ||
                            (error.message && error.message.includes('403')) ||
                            (error.message && error.message.includes('Forbidden'))
                        );
                        
                        try {
                            // Detach player from video element first
                            player.detach();
                            player.destroy();
                        } catch (e) {
                            console.error('Error destroying player:', e);
                        }
                        playerRef.current = null;
                        
                        // Clear video source and ensure clean state
                        video.src = '';
                        video.removeAttribute('src');
                        
                        // Small delay to ensure Shaka Player is fully cleaned up
                        setTimeout(() => {
                            // Fallback to MP4
                            if (playbackData.mp4?.url) {
                                if (is403Error) {
                                    console.log('🚫 403 Forbidden - HLS manifest or segments are not accessible.');
                                    console.log('📹 Falling back to MP4 playback (this works with signed URLs).');
                                    console.log('💡 Check: Ensure backend hlsManifest endpoint is correctly pre-signing all segment URLs.');
                                    // Don't show toast for 403 - it's expected and MP4 will work
                                } else if (isCorsError) {
                                    console.log('🚫 CORS error - falling back to MP4. Ensure CORS is configured on S3 bucket.');
                                    toast.info('Switched to MP4 playback');
                                } else {
                                    console.log('🔄 Falling back to MP4 after HLS load error');
                                    toast.info('Switched to MP4 playback');
                                }
                                console.log('📹 Setting MP4 source:', playbackData.mp4.url);
                                video.src = playbackData.mp4.url;
                                video.load(); // Force video to reload with new source
                            } else {
                                toast.error('Failed to load video - no MP4 URL available');
                            }
                        }, 100);
                    });

            } catch (error) {
                console.error('❌ Error initializing Shaka Player:', error);
                // Fallback to MP4
                if (playbackData.mp4?.url) {
                    console.log('🔄 Using MP4 fallback after Shaka Player init error');
                    video.src = playbackData.mp4.url;
                } else {
                    toast.error('Failed to initialize video player');
                }
            }
        } else if (playbackData.mp4?.url) {
            // Use MP4 directly (no Shaka Player needed)
            console.log('📹 Using MP4 playback:', playbackData.mp4.url);
            video.src = playbackData.mp4.url;
        }

        return () => {
            // Cleanup Shaka Player
            if (playerRef.current) {
                try {
                    playerRef.current.destroy();
                } catch (error) {
                    console.error('Error destroying Shaka Player in cleanup:', error);
                }
                playerRef.current = null;
            }
        };
    }, [playbackData, showPlayerModal]);

    const formatDuration = (startTime: string, stopTime: any) => {
        try {
            const start = new Date(startTime);
            const stop = stopTime?.toDate ? stopTime.toDate() : new Date(stopTime);
            const diffMs = stop.getTime() - start.getTime();
            const diffMins = Math.floor(diffMs / 60000);
            const hours = Math.floor(diffMins / 60);
            const mins = diffMins % 60;
            return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
        } catch {
            return 'Unknown';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'stopped': return 'bg-green-100 text-green-800';
            case 'transcoded': return 'bg-green-100 text-green-800'; // Transcoded = ready to play
            case 'recording': return 'bg-red-100 text-red-800';
            case 'processing': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'stopped': return 'Ready';
            case 'transcoded': return 'Ready'; // Transcoded = ready to play
            case 'recording': return 'Recording';
            case 'processing': return 'Processing';
            default: return status;
        }
    };
    
    // Check if recording is playable (stopped or transcoded)
    const isPlayable = (status: string) => {
        return status === 'stopped' || status === 'transcoded';
    };

    const filteredRecordings = recordings.filter(recording => {
        if (filterStatus === 'all') return true;
        if (filterStatus === 'stopped') return isPlayable(recording.status); // Include both 'stopped' and 'transcoded'
        if (filterStatus === 'processing') return !isPlayable(recording.status); // Exclude playable recordings
        return true;
    });

    // Apply maxItems limit if specified
    const displayRecordings = maxItems ? filteredRecordings.slice(0, maxItems) : filteredRecordings;

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                            <polygon points="23 7 16 12 23 17 23 7"></polygon>
                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Recorded Sessions</h3>
                        <p className="text-sm text-gray-600">View and manage your recorded Meet+ sessions</p>
                    </div>
                </div>

                {/* Filter - Only show when not limited */}
                {!maxItems && (
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Filter:</span>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value as any)}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                        >
                            <option value="all">All Recordings ({recordings.length})</option>
                            <option value="stopped">Ready to Watch ({recordings.filter(r => isPlayable(r.status)).length})</option>
                            <option value="processing">Processing ({recordings.filter(r => !isPlayable(r.status)).length})</option>
                        </select>
                    </div>
                )}
            </div>

            {/* Recordings List */}
            <div className="space-y-1.5">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                        <span className="ml-3 text-gray-600">Loading recordings...</span>
                    </div>
                ) : filteredRecordings.length === 0 ? (
                    <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                                <polygon points="23 7 16 12 23 17 23 7"></polygon>
                                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                            </svg>
                        </div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No recordings found</h4>
                        <p className="text-gray-600 mb-4">
                            {filterStatus === 'all' 
                                ? 'Start recording your meetings to see them here.' 
                                : `No recordings with this status available.`
                            }
                        </p>
                        {filterStatus !== 'all' && (
                            <button
                                onClick={() => setFilterStatus('all')}
                                className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                            >
                                View all recordings
                            </button>
                        )}
                    </div>
                ) : (
                    displayRecordings.map((recording) => (
                        <div key={recording.id} className="bg-white border border-gray-200 rounded-md p-2.5 hover:border-gray-300 hover:shadow-sm transition-all">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <h4 className="font-medium text-gray-900 text-sm truncate">{recording.meeting_title}</h4>
                                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full flex-shrink-0 ${getStatusColor(recording.status)}`}>
                                            {getStatusLabel(recording.status)}
                                        </span>
                                    </div>
                                    
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-600">
                                        <div className="flex items-center gap-1">
                                            <span className="text-gray-400">By</span>
                                            <span className="font-medium text-gray-700">{recording.created_by}</span>
                                        </div>
                                        <span className="text-gray-300">•</span>
                                        <div className="flex items-center gap-1">
                                            <span className="text-gray-400">Duration</span>
                                            <span className="font-medium text-gray-700">
                                                {formatDuration(recording.started_at, recording.stopped_at)}
                                            </span>
                                        </div>
                                        <span className="text-gray-300">•</span>
                                        <div className="flex items-center gap-1">
                                            <span className="text-gray-400">Date</span>
                                            <span className="font-medium text-gray-700">
                                                {new Date(recording.started_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <span className="text-gray-300">•</span>
                                        <div className="flex items-center gap-1">
                                            <span className="text-gray-400">Time</span>
                                            <span className="font-medium text-gray-700">
                                                {new Date(recording.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {isPlayable(recording.status) && (
                                        <button
                                            onClick={() => playRecording(recording)}
                                            className="p-1.5 text-white bg-gradient-to-r from-purple-500 to-blue-600 rounded-md hover:from-purple-600 hover:to-blue-700 transition-all shadow-sm hover:shadow"
                                            title="Play recording"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                            </svg>
                                        </button>
                                    )}
                                    <button
                                        onClick={() => deleteRecording(recording.id, recording.meeting_title)}
                                        disabled={deletingRecordingId === recording.id}
                                        className="p-1.5 text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Delete recording"
                                    >
                                        {deletingRecordingId === recording.id ? (
                                            <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* View More Link - Removed in favor of custom button in parent */}

            {/* Video Player Modal */}
            {showPlayerModal && selectedRecording && (
                <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 w-full max-w-6xl mx-4 shadow-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-900">
                                {selectedRecording.meeting_title}
                            </h3>
                            <button
                                onClick={() => setShowPlayerModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 6L6 18M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>

                        <div className="aspect-video bg-black rounded-lg overflow-hidden shadow-xl">
                            {isLoadingPlaybackUrl ? (
                                <div className="flex items-center justify-center h-full text-white">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                                        <p className="text-lg font-medium mb-2">Loading playback URL...</p>
                                        <p className="text-sm text-gray-300">Please wait while we prepare the recording.</p>
                                    </div>
                                </div>
                            ) : playbackData && (playbackData.hls?.manifestUrl || playbackData.mp4?.url) ? (
                                <video
                                    ref={(el) => {
                                        videoRef.current = el;
                                        if (el) {
                                            console.log('📹 Video element ref set:', el);
                                        }
                                    }}
                                    controls
                                    autoPlay
                                    className="w-full h-full"
                                    preload="metadata"
                                    onLoadedMetadata={() => console.log('📹 Video metadata loaded')}
                                    onLoadedData={() => console.log('📹 Video data loaded')}
                                    onCanPlay={() => console.log('✅ Video can play')}
                                    onError={(e) => {
                                        console.error('❌ Video element error:', {
                                            error: e.currentTarget.error,
                                            code: e.currentTarget.error?.code,
                                            message: e.currentTarget.error?.message
                                        });
                                    }}
                                >
                                    Your browser does not support the video tag.
                                </video>
                            ) : (
                                <div className="flex items-center justify-center h-full text-white">
                                    <div className="text-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4">
                                            <polygon points="23 7 16 12 23 17 23 7"></polygon>
                                            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                                        </svg>
                                        <p className="text-xl font-medium mb-2">Video not available</p>
                                        <p className="text-sm text-gray-300 mb-4">
                                            {isLoadingPlaybackUrl 
                                                ? 'Loading playback URL...' 
                                                : 'Recording file is still processing or not available.'}
                                        </p>
                                        {!isLoadingPlaybackUrl && (
                                            <button
                                                onClick={() => playRecording(selectedRecording!)}
                                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                                            >
                                                Retry
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <span className="text-gray-500 block mb-1">Recorded By</span>
                                <span className="font-semibold text-gray-900">{selectedRecording.created_by}</span>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <span className="text-gray-500 block mb-1">Duration</span>
                                <span className="font-semibold text-gray-900">
                                    {formatDuration(selectedRecording.started_at, selectedRecording.stopped_at)}
                                </span>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <span className="text-gray-500 block mb-1">Date</span>
                                <span className="font-semibold text-gray-900">
                                    {new Date(selectedRecording.started_at).toLocaleDateString()}
                                </span>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <span className="text-gray-500 block mb-1">Time</span>
                                <span className="font-semibold text-gray-900">
                                    {new Date(selectedRecording.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecordedSessions;

